import CrossCircleIcon from "@atlaskit/icon/core/cross-circle";
import EditIcon from "@atlaskit/icon/core/edit";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useEffect, useState} from "react";
import {useNavigate} from "@tanstack/react-router";
import {GenericModal} from "~/components/genericModal";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {
    CallStatusEnum,
    DHIRAAGU__EVENT__CALLS__CANCELLATION_REMARKS__COLUMN_ID,
    DHIRAAGU__EVENT__CALLS__CANCELLED_BY__COLUMN_ID,
    DHIRAAGU__EVENT__CALLS__CANCELLED_ON__COLUMN_ID,
    DHIRAAGU__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
    DHIRAAGU__CALL__LOG__FORM__ID,
    DHIRAAGU__EVENT__CALLS__STATUS__COLUMN_ID,
    DhiraaguEventTypes,
    isDhiraaguWorkspace,
} from "~/domainModels/workspaceSpecific/dhiraagu";
import {configFormHref} from "~/workspaceConfig/configHref";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeDropdownItem} from "@vesta/ananke/components/anankeDropdownItem";
import {AnankeField} from "@vesta/ananke/components/anankeField";
import {AnankeTextArea} from "@vesta/ananke/components/anankeTextArea";
import {useAnankeFlags} from "@vesta/ananke/hooks/useAnankeFlags";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {getCurrentIsoTimestamp} from "@vesta/global-common-typescript/utilities/utilities";
import {updateEvent, type EventWithEventContainer} from "~/utilities/apis/api";

function isScheduledCallOverdue(event: EventWithEventContainer): boolean {
    const callStatus = event.additionalColumnValues[DHIRAAGU__EVENT__CALLS__STATUS__COLUMN_ID] as string | undefined;
    if (callStatus !== CallStatusEnum.SCHEDULED) {
        return false;
    }

    const scheduledOn = event.additionalColumnValues[DHIRAAGU__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID] as string | undefined;
    if (scheduledOn === undefined || scheduledOn === null) {
        return false;
    }

    const scheduledDate = new Date(scheduledOn);
    const now = new Date();

    const scheduledDay = new Date(scheduledDate.getFullYear(), scheduledDate.getMonth(), scheduledDate.getDate());
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return scheduledDay < today;
}

export function useAutoCancelOverdueCall({
    event,
    queryKeyPrefix,
    enabled,
}: {
    event: EventWithEventContainer;
    queryKeyPrefix: Array<unknown>;
    enabled: boolean;
}) {
    const {heimdallUser} = useWorkspaceUser();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {workspace} = useWorkspace();
    const queryClient = useQueryClient();
    const [hasAutoCancelled, setHasAutoCancelled] = useState(false);

    const autoCancelMutation = useMutation({
        mutationFn: async () => {
            const result = await updateEvent({
                accessToken: heimdallUser.accessToken,
                intellsysBaseUrl: intellsysBaseUrl,
                eventId: event.id,
                templateId: event.templateId,
                workspaceId: workspace.id,
                data: {
                    [DHIRAAGU__EVENT__CALLS__STATUS__COLUMN_ID]: CallStatusEnum.CANCELLED,
                    [DHIRAAGU__EVENT__CALLS__CANCELLED_ON__COLUMN_ID]: getCurrentIsoTimestamp(),
                    [DHIRAAGU__EVENT__CALLS__CANCELLATION_REMARKS__COLUMN_ID]: "Auto-cancelled: Scheduled call was not logged within the same day",
                } as Record<Uuid, unknown>,
            });

            if (result.success !== true) {
                throw result.error;
            }
        },
        onSuccess: () => {
            setHasAutoCancelled(true);
            queryClient.invalidateQueries({
                queryKey: queryKeyPrefix,
            });
        },
    });

    useEffect(() => {
        if (enabled === false) {
            return;
        }
        if (hasAutoCancelled === true) {
            return;
        }
        if (isDhiraaguWorkspace(workspace) === false) {
            return;
        }
        if (isScheduledCallOverdue(event) === true) {
            autoCancelMutation.mutate();
        }
    }, [enabled, event.id, hasAutoCancelled]);

    return {
        isAutoCancelling: autoCancelMutation.isPending,
        hasAutoCancelled: hasAutoCancelled,
    };
}

export function DhiraaguCancelCallDropdownItem({
    event,
    queryKeyPrefix,
    onSuccess,
    onOpenCancelModal,
}: {
    event: EventWithEventContainer;
    queryKeyPrefix: Array<unknown>;
    onSuccess?: () => void;
    onOpenCancelModal?: (event: EventWithEventContainer, queryKeyPrefix: Array<unknown>) => void;
}) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const {workspace} = useWorkspace();

    if (!isDhiraaguWorkspace(workspace)) {
        return null;
    }

    const callStatus = event.additionalColumnValues[DHIRAAGU__EVENT__CALLS__STATUS__COLUMN_ID] as string | undefined;

    if (callStatus !== CallStatusEnum.SCHEDULED) {
        return null;
    }

    const handleOpenModal = () => {
        if (onOpenCancelModal !== undefined) {
            onOpenCancelModal(event, queryKeyPrefix);
        } else {
            setIsModalOpen(true);
        }
    };

    return (
        <>
            <AnankeDropdownItem
                onClick={() => {
                    handleOpenModal();
                }}
            >
                <span className="flex items-center gap-2">
                    <CrossCircleIcon label="" />
                    Cancel Call
                </span>
            </AnankeDropdownItem>

            {onOpenCancelModal === undefined ? (
                <CancelCallModal
                    event={event}
                    queryKeyPrefix={queryKeyPrefix}
                    isOpen={isModalOpen}
                    setIsOpen={setIsModalOpen}
                    onSuccess={onSuccess}
                />
            ) : null}
        </>
    );
}

export function DhiraaguLogCallDropdownItem({event}: {event: EventWithEventContainer}) {
    const {workspace} = useWorkspace();
    const navigate = useNavigate();

    if (isDhiraaguWorkspace(workspace) === false) {
        return null;
    }
    if (event.templateId !== (DhiraaguEventTypes.call as Uuid)) {
        return null;
    }

    const callStatus = event.additionalColumnValues[DHIRAAGU__EVENT__CALLS__STATUS__COLUMN_ID] as string | undefined;
    if (callStatus !== CallStatusEnum.SCHEDULED) {
        return null;
    }

    return (
        <AnankeDropdownItem
            onClick={() => {
                navigate({
                    to: configFormHref(workspace.id, DHIRAAGU__CALL__LOG__FORM__ID, {
                        eventContainerId: event.eventContainer.id,
                        eventId: event.id,
                    }),
                });
            }}
            elemBefore={<EditIcon label="" />}
        >
            Log
        </AnankeDropdownItem>
    );
}

export function DhiraaguCancelCallButton({
    event,
    queryKeyPrefix,
    onSuccess,
}: {
    event: EventWithEventContainer;
    queryKeyPrefix: Array<unknown>;
    onSuccess?: () => void;
}) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const {workspace} = useWorkspace();

    if (!isDhiraaguWorkspace(workspace)) {
        return null;
    }

    const callStatus = event.additionalColumnValues[DHIRAAGU__EVENT__CALLS__STATUS__COLUMN_ID] as string | undefined;

    if (callStatus !== CallStatusEnum.SCHEDULED) {
        return null;
    }

    return (
        <>
            <AnankeButton
                type="button"
                appearance="danger"
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setIsModalOpen(true);
                }}
            >
                Cancel Call
            </AnankeButton>

            <CancelCallModal
                event={event}
                queryKeyPrefix={queryKeyPrefix}
                isOpen={isModalOpen}
                setIsOpen={setIsModalOpen}
                onSuccess={onSuccess}
            />
        </>
    );
}

export function CancelCallModal({
    event,
    queryKeyPrefix,
    isOpen,
    setIsOpen,
    onSuccess,
}: {
    event: EventWithEventContainer;
    queryKeyPrefix: Array<unknown>;
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    onSuccess?: () => void;
}) {
    const {heimdallUser} = useWorkspaceUser();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {workspace} = useWorkspace();
    const queryClient = useQueryClient();
    const {showFlag} = useAnankeFlags();

    const [remarks, setRemarks] = useState("");

    const cancelMutation = useMutation({
        mutationFn: async () => {
            const result = await updateEvent({
                accessToken: heimdallUser.accessToken,
                intellsysBaseUrl: intellsysBaseUrl,
                eventId: event.id,
                templateId: event.templateId,
                workspaceId: workspace.id,
                data: {
                    [DHIRAAGU__EVENT__CALLS__STATUS__COLUMN_ID]: CallStatusEnum.CANCELLED,
                    [DHIRAAGU__EVENT__CALLS__CANCELLED_BY__COLUMN_ID]: heimdallUser.id,
                    [DHIRAAGU__EVENT__CALLS__CANCELLED_ON__COLUMN_ID]: getCurrentIsoTimestamp(),
                    [DHIRAAGU__EVENT__CALLS__CANCELLATION_REMARKS__COLUMN_ID]: remarks,
                } as Record<Uuid, unknown>,
            });

            if (result.success !== true) {
                throw result.error;
            }
        },
        onSuccess: () => {
            showFlag({
                title: "Success",
                description: "Call cancelled successfully",
                appearance: "success",
                isAutoDismiss: true,
                autoDismissSeconds: 3,
            });
            setIsOpen(false);
            setRemarks("");
            queryClient.invalidateQueries({
                queryKey: queryKeyPrefix,
            });
            onSuccess?.();
        },
        onError: (error) => {
            showFlag({
                title: "Error",
                description: error.message,
                appearance: "error",
                isAutoDismiss: true,
                autoDismissSeconds: 3,
            });
        },
    });

    if (isOpen === false) {
        return null;
    }

    return (
        <GenericModal
            opened={isOpen}
            onClose={() => {
                setIsOpen(false);
                setRemarks("");
            }}
            title="Cancel Scheduled Call"
            zIndex={10000}
            size="md"
        >
            <div className="grid gap-ds-space-200 pt-ds-space-200">
                <AnankeField
                    id="cancel-call-remarks"
                    label="Cancellation Remarks"
                    isRequired={true}
                >
                    <AnankeTextArea
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder="Enter reason for cancellation..."
                        rows={4}
                    />
                </AnankeField>

                <div className="grid grid-cols-2 gap-ds-space-150 pt-ds-space-100">
                    <AnankeButton
                        type="button"
                        appearance="subtle"
                        onClick={() => {
                            setIsOpen(false);
                            setRemarks("");
                        }}
                    >
                        Cancel
                    </AnankeButton>
                    <AnankeButton
                        type="button"
                        appearance="danger"
                        onClick={() => {
                            cancelMutation.mutate();
                        }}
                        isDisabled={remarks.trim() === "" || cancelMutation.isPending}
                        isLoading={cancelMutation.isPending}
                    >
                        Confirm Cancellation
                    </AnankeButton>
                </div>
            </div>
        </GenericModal>
    );
}
